import type { ReactNode } from 'react';
import { Button, FilterMultiSelect, ViewTab, ViewTabList } from '@wundo/ui';
import { Calendar, List } from 'lucide-react';
import type { MeetingBucket } from '@wundo/shared/calendar.ts';
import { m } from '@/paraglide/messages.js';
import { filterLabel, filterRows, type AgendaFilterValue } from './agendaFilter.ts';

export type AgendaView = 'week' | 'list';

/*
 * Each view wears the shape it draws: a calendar for the grid, a list for the
 * list. The label still names it — the icon is what makes the pair findable at
 * a glance, never the only thing saying which is which.
 */
const VIEWS = [
  { value: 'week', label: m.agenda_view_week(), icon: Calendar },
  { value: 'list', label: m.agenda_view_list(), icon: List },
] as const satisfies readonly {
  value: AgendaView;
  label: string;
  icon: typeof Calendar;
}[];

export interface AgendaToolbarProps {
  view: AgendaView;
  onViewChange: (view: AgendaView) => void;
  /* Which drawers of the calendar are showing, for this visit. */
  buckets: readonly MeetingBucket[];
  onFilterSelect: (value: AgendaFilterValue) => void;
  onToday: () => void;
  /*
   * What this agenda lets the user do to it, if anything. A slot rather than a
   * flag: the toolbar lays the row out and has no business knowing that the one
   * action there is today is adding a meeting by hand.
   */
  action?: ReactNode;
  /* Where the week came from, when a calendar put it there. */
  source?: ReactNode;
}

/*
 * The filter and the view are independent controls: the filter changes *what*
 * you see, the switch changes *how*. Changing one must never reset the other —
 * being thrown out of week view because you ticked a category is the bug this
 * shape avoids.
 */
export function AgendaToolbar({
  view,
  onViewChange,
  buckets,
  onFilterSelect,
  onToday,
  action,
  source,
}: AgendaToolbarProps) {
  return (
    <div className='flex flex-wrap items-center gap-3'>
      <ViewTabList aria-label={m.agenda_view_label()}>
        {VIEWS.map((option) => (
          <ViewTab
            key={option.value}
            active={option.value === view}
            onClick={() => onViewChange(option.value)}
          >
            <option.icon className='size-3.5' aria-hidden='true' />
            {option.label}
          </ViewTab>
        ))}
      </ViewTabList>
      <Button variant='quiet' size='sm' onClick={onToday}>
        {m.agenda_today()}
      </Button>
      <FilterMultiSelect<AgendaFilterValue>
        label={m.agenda_filter_label()}
        buttonLabel={filterLabel(buckets)}
        options={filterRows(buckets)}
        onSelect={onFilterSelect}
      />
      {/*
        Pushed to the far edge: what you can do to the week and where it came
        from are about the whole page, not about the controls beside them. The
        wrapper only exists when one of them does, so nothing pushes an empty
        gap into the row.
      */}
      {(action || source) && (
        <div className='ml-auto flex flex-wrap items-center gap-3'>
          {action}
          {source}
        </div>
      )}
    </div>
  );
}
